import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState, LoadingState } from './EmptyState';
import { ErrorBanner } from './ErrorBanner';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatNumber } from '@/i18n/format';
import type { AsyncResult } from '@/api/useApi';
import type { Page } from '@/api/types';

export type Column<T> = {
  readonly key: string;
  readonly header: string;
  readonly render: (row: T) => ReactNode;
  readonly align?: 'left' | 'right';
};

/**
 * The one list-table component every "List" screen renders through -
 * consistent empty/loading/error states, row-click-to-detail, and
 * server-side pagination (Part B: "server-side pagination where needed,
 * clear empty states, row-click-to-detail - don't dump 15 unprioritized
 * columns"). Callers pass a bounded, curated `columns` array per screen.
 */
export function DataTable<T extends { id: string }>({
  result,
  columns,
  getRowHref,
  limit,
  offset,
  onPageChange,
  emptyTitle,
  emptyHint,
}: {
  result: AsyncResult<Page<T>> & { reload: () => void };
  columns: readonly Column<T>[];
  getRowHref?: (row: T) => string;
  limit: number;
  offset: number;
  onPageChange: (offset: number) => void;
  emptyTitle?: string;
  emptyHint?: string;
}): JSX.Element {
  const navigate = useNavigate();
  const { t, locale } = useLanguage();

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;

  const { rows, total: rawTotal } = result.data;
  if (rows.length === 0) return <EmptyState title={emptyTitle ?? t('common.nothingHereYet')} hint={emptyHint} />;

  const total = Number.isFinite(rawTotal) ? rawTotal : offset + rows.length;
  const from = offset + 1;
  const to = Math.min(offset + rows.length, total);

  return (
    <div>
      <div className="table-scroll"><table>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={c.align === 'right' ? { textAlign: 'right' } : undefined}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const href = getRowHref?.(row);
            return (
              <tr
                key={row.id}
                tabIndex={href ? 0 : undefined}
                onClick={href ? () => navigate(href) : undefined}
                onKeyDown={
                  href
                    ? (e) => {
                        if (e.key === 'Enter') navigate(href);
                      }
                    : undefined
                }
              >
                {columns.map((c) => (
                  <td key={c.key} style={c.align === 'right' ? { textAlign: 'right' } : undefined}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table></div>
      <div className="pagination">
        <span>
          {formatNumber(from, locale)}–{formatNumber(to, locale)} {t('common.of')} {formatNumber(total, locale)}
        </span>
        <button type="button" disabled={offset === 0} onClick={() => onPageChange(Math.max(0, offset - limit))}>
          {t('common.previous')}
        </button>
        <button type="button" disabled={to >= total} onClick={() => onPageChange(offset + limit)}>
          {t('common.next')}
        </button>
      </div>
    </div>
  );
}
